/**
 * analyticsFormatters - Display name mappers, channel config, and page title helpers
 * for the analytics dashboard.
 */

// ==================== Source Display Names ====================

const SOURCE_DISPLAY_NAMES: Record<string, string> = {
  // Legacy lowercase values from old parseReferrerSource
  google: 'Google',
  bing: 'Bing',
  yahoo: 'Yahoo',
  duckduckgo: 'DuckDuckGo',
  baidu: 'Baidu',
  facebook: 'Facebook',
  fb: 'Facebook',
  twitter: 'X / Twitter',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  reddit: 'Reddit',
  youtube: 'YouTube',
  email: 'Email',
  direct: 'Direct',
  other: 'Other',
}

/**
 * Map a source identifier (legacy lowercase or new proper-case) to a display-friendly name
 */
export function getSourceDisplayName(source: string): string {
  // Check legacy map first (lowercase)
  const mapped = SOURCE_DISPLAY_NAMES[source.toLowerCase()]
  if (mapped) return mapped
  // Already proper-cased from classifySource — return as-is
  return source
}

// ==================== Page Display Names ====================

/**
 * Returns the best display name for a page:
 * title if available, otherwise a humanized path
 */
export function getPageDisplayName(path: string, title?: string | null): string {
  if (title && title.trim()) {
    // Strip common suffixes like " | My Site" or " - My Site"
    return title.split(/\s[|–—-]\s/)[0].trim()
  }
  return humanizePath(path)
}

/**
 * Humanize a URL path into a readable name
 * "/" -> "Home"
 * "/about-us" -> "About Us"
 * "/blog/my-first-post" -> "My First Post"
 */
export function humanizePath(path: string): string {
  if (path === '/' || path === '') return 'Home'
  // Take the last segment
  const segments = path.split('/').filter(Boolean)
  const last = segments[segments.length - 1]
  // Replace hyphens/underscores with spaces and capitalize
  return last
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

// ==================== Channel Configuration ====================

export type ChannelKey = 'organic_search' | 'paid_search' | 'social' | 'paid_social' | 'email' | 'referral' | 'direct' | 'display'

export interface ChannelConfig {
  label: string
  color: string       // Tailwind color class stem (e.g. "blue" for bg-blue-500)
  hexColor: string    // For Recharts
}

export const CHANNEL_CONFIG: Record<ChannelKey, ChannelConfig> = {
  organic_search: { label: 'Organic Search', color: 'emerald', hexColor: '#10b981' },
  paid_search:    { label: 'Paid Search',    color: 'orange',  hexColor: '#f97316' },
  social:         { label: 'Social',         color: 'blue',    hexColor: '#3b82f6' },
  paid_social:    { label: 'Paid Social',    color: 'amber',  hexColor: '#f59e0b' },
  email:          { label: 'Email',          color: 'amber',   hexColor: '#f59e0b' },
  referral:       { label: 'Referral',       color: 'cyan',    hexColor: '#06b6d4' },
  direct:         { label: 'Direct',         color: 'slate',   hexColor: '#64748b' },
  display:        { label: 'Display',        color: 'pink',    hexColor: '#ec4899' },
}

export function getChannelLabel(channel: string): string {
  return CHANNEL_CONFIG[channel as ChannelKey]?.label || channel.charAt(0).toUpperCase() + channel.slice(1).replace('_', ' ')
}

export function getChannelColor(channel: string): string {
  return CHANNEL_CONFIG[channel as ChannelKey]?.hexColor || '#94a3b8'
}

// Ordered list of channel keys for consistent display
export const CHANNEL_ORDER: ChannelKey[] = [
  'organic_search', 'social', 'direct', 'referral', 'email', 'paid_search', 'paid_social', 'display'
]
